import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Download, Share2, User, Calendar, FileText, Tag, Edit3, Save, X, Trash2, Settings, Palette, Zap, Activity, BarChart3, Radio, Waves, Upload, Image, Users, ChevronDown, Check, Heart, MessageCircle, Link, ZoomIn, Star, LogIn, Music, DollarSign, Play, Pause, Lock, Disc3 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import trackingService from '../services/tracking';
import FileUpload from '../components/FileUpload';
import DownloadPaywall from '../components/DownloadPaywall';
import ShareModal from '../components/ShareModal';
import AccessRequiredModal from '../components/AccessRequiredModal';
import LatestDeepWaveSongs from '../components/LatestDeepWaveSongs';
import LatestDeepWaveVideos from '../components/LatestDeepWaveVideos';
import LatestDeepWaveImages from '../components/LatestDeepWaveImages';
import { getAccessLabel } from '../utils/accessLabels';
import ContributingArtistSelector from '../components/ContributingArtistSelector';
import ContributingArtistsDisplay from '../components/ContributingArtistsDisplay';
import SignupInviteModal from '../components/SignupInviteModal';
import PrivacySettings from '../components/PrivacySettings';
import CopyrightSettings from '../components/CopyrightSettings';
import api from '../services/api';
import { currentPagePath } from '../utils/redirect';
import { MUSIC_GENRES } from '../utils/artistTypes';
import { parseTagsSafely } from '../utils/parseTags';

const MediaDisplay = () => {
    const { type, username, filename } = useParams();
    const { isAuthenticated, user } = useAuth();

    // Genre options (same as upload form)
    const genres = MUSIC_GENRES;

    const toggleGenre = (genre) => {
        setEditFormData(prev => ({
            ...prev,
            genre: prev.genre.includes(genre)
                ? prev.genre.filter(g => g !== genre)
                : [...prev.genre, genre]
        }));
    };

    const removeGenre = (genre) => {
        setEditFormData(prev => ({
            ...prev,
            genre: prev.genre.filter(g => g !== genre)
        }));
    };

    const [media, setMedia] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editFormData, setEditFormData] = useState({
        title: '',
        description: '',
        lyrics: '',
        tags: '',
        artist: '',
        album: '',
        genre: [],
        year: '',
        bpm: '',
        key_signature: '',
        track_number: '',
        duration: '',
        contributingArtists: [],
        hyperlinks: [],
        accessLevel: 'public',
        accessPassword: '',
        accessHint: '',
        isPaid: false,
        price: '',
        currency: 'USD'
    });
    const [mediaStats, setMediaStats] = useState(null);
    const [showImageEditor, setShowImageEditor] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [downloadPaywall, setDownloadPaywall] = useState({ isOpen: false, media: null });
    const [shareModal, setShareModal] = useState({ isOpen: false, media: null });
    const [signupInvite, setSignupInvite] = useState({ isOpen: false, contentType: 'audio' });
    const [comments, setComments] = useState([]);
    const [loadingComments, setLoadingComments] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);
    const [genreDropdownOpen, setGenreDropdownOpen] = useState(false);
    const [hyperlinks, setHyperlinks] = useState([]);
    const [loadingHyperlinks, setLoadingHyperlinks] = useState(false);
    const [isImageExpanded, setIsImageExpanded] = useState(false);
    // Zoom/pan state for the expanded fullscreen image view — scroll to zoom
    // in on detail, drag to pan once zoomed, double-click to reset.
    const [zoomScale, setZoomScale] = useState(1);
    const [zoomOffset, setZoomOffset] = useState({ x: 0, y: 0 });
    const dragStateRef = useRef(false);
    const [descriptionTab, setDescriptionTab] = useState('description');
    const [accessRequired, setAccessRequired] = useState(false);
    const [showAccessModal, setShowAccessModal] = useState(false);
    const [passwordInput, setPasswordInput] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const [audioCurrentTime, setAudioCurrentTime] = useState(0);
    const [audioDuration, setAudioDuration] = useState(0);
    const genreDropdownRef = useRef(null);
    const audioRef = useRef(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const animationRef = useRef(null);
    
    // Visualizer state
    const [visualizerStyle, setVisualizerStyle] = useState('bars');
    const [showVisualizerOptions, setShowVisualizerOptions] = useState(false);
    const [visualizerSettings, setVisualizerSettings] = useState({
        sensitivity: 1.2,
        smoothing: 0.8,
        colorScheme: 'wave',
        glowIntensity: 0.5,
        particleCount: 50,
        lineThickness: 2,
        mirrorMode: false,
        autoColor: true,
        bassBoost: 1.0,
        fftSize: 256
    });

    // Close genre dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (genreDropdownRef.current && !genreDropdownRef.current.contains(event.target)) {
                setGenreDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Handle escape key to exit expanded view and disable save shortcuts
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && isImageExpanded) {
                setIsImageExpanded(false);
            }
            
            // Disable common save/download keyboard shortcuts when image is expanded
            if (isImageExpanded && (
                (event.ctrlKey && (event.key === 's' || event.key === 'S')) ||
                (event.ctrlKey && event.shiftKey && (event.key === 's' || event.key === 'S')) ||
                (event.ctrlKey && (event.key === 'a' || event.key === 'A')) ||
                event.key === 'F12'
            )) {
                event.preventDefault();
                event.stopPropagation();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isImageExpanded]);

    // Reset zoom/pan every time the expanded view opens or closes, so it
    // never carries over stale zoom from a previous look at the image.
    useEffect(() => {
        setZoomScale(1);
        setZoomOffset({ x: 0, y: 0 });
    }, [isImageExpanded]);

    const handleImageZoomWheel = (e) => {
        e.preventDefault();
        setZoomScale(prev => {
            const next = Math.min(4, Math.max(1, prev - e.deltaY * 0.0015));
            if (next === 1) setZoomOffset({ x: 0, y: 0 });
            return next;
        });
    };

    // Tracked on window rather than the image itself — once zoomed in, a fast
    // drag easily carries the cursor past the image's own bounds, and an
    // element-scoped mousemove/mouseup would silently stop following.
    const handleImageZoomMouseDown = (e) => {
        e.stopPropagation();
        if (zoomScale <= 1) return;
        const startX = e.clientX;
        const startY = e.clientY;
        const originX = zoomOffset.x;
        const originY = zoomOffset.y;
        dragStateRef.current = true;

        const handleMove = (moveEvent) => {
            setZoomOffset({ x: originX + (moveEvent.clientX - startX), y: originY + (moveEvent.clientY - startY) });
        };
        const handleUp = () => {
            dragStateRef.current = false;
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
        };
        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
    };

    const handleImageZoomDoubleClick = (e) => {
        e.stopPropagation();
        setZoomScale(1);
        setZoomOffset({ x: 0, y: 0 });
    };

    useEffect(() => {
        const fetchMediaMetadata = async () => {
            try {
                
                // Map folder names to API types
                const mediaType = type === 'videos' ? 'video' :
                                 type === 'audio' ? 'audio' :
                                 type === 'images' || type === 'profiles' ? 'image' : type;

                // Use the access-aware view endpoint so password-protected /
                // private items are handled correctly instead of showing 404.
                const response = await api.get(`/upload/view/${mediaType}/${username}/${filename}`);
                const result = response.data;

                if (result.success && result.data) {
                    const mediaData = result.data;

                    // Private / password-protected / pure-purchase — no file_url is sent
                    // by the backend for these, so show the full lock screen and stop here.
                    // Images always hard-gate too: unlike audio/video there's no play
                    // interaction to intercept, so the full image would otherwise render
                    // in the clear the moment file_url is present.
                    const isHardGate = mediaData.access_required &&
                        (mediaData.access_type === 'private' || mediaData.access_type === 'password' || mediaData.access_type === 'payment' || mediaType === 'image');
                    if (isHardGate) {
                        setMedia({ ...mediaData, type: mediaType, file_url: null });
                        setAccessRequired(true);
                        setLoading(false);
                        return;
                    }

                    // Login / subscription gates: the backend still sends file_url for
                    // these (same client-side gating model used across the rest of the
                    // site), so render the page normally below and just track that it's
                    // gated for the small indicator + play lock.
                    setAccessRequired(!!mediaData.access_required);

                    // Use the file_url from database, ensure it's absolute HTTPS URL
                        let fileUrl = mediaData.file_url;
                        
                        // If it's a relative URL, make it absolute
                        if (fileUrl && !fileUrl.startsWith('http')) {
                            if (!fileUrl.startsWith('/')) {
                                fileUrl = '/' + fileUrl;
                            }
                            fileUrl = window.location.origin + fileUrl;
                        }
                        
                        // Ensure HTTPS for secure contexts
                        if (fileUrl && fileUrl.startsWith('http://') && window.location.protocol === 'https:') {
                            fileUrl = fileUrl.replace('http://', 'https://');
                        }
                        
                        
                        const mediaObj = { 
                            ...mediaData, 
                            type: mediaType,
                            file_url: fileUrl 
                        };
                        
                        
                        setMedia(mediaObj);
                        
                        // Initialize edit form data
                        const parsedTags = parseTagsSafely(mediaData.tags);
                        const tagsString = parsedTags.join(', ');
                        
                        setEditFormData({
                            title: mediaData.title || '',
                            description: mediaData.description || '',
                            lyrics: mediaData.lyrics || '',
                            tags: tagsString,
                            artist: mediaData.artist || '',
                            album: mediaData.album || '',
                            genre: mediaData.genre ? mediaData.genre.split(',').map(g => g.trim()) : [],
                            year: mediaData.year || '',
                            bpm: mediaData.bpm || '',
                            key_signature: mediaData.key_signature || '',
                            track_number: mediaData.track_number || '',
                            duration: mediaData.duration || '',
                            contributingArtists: [],
                            hyperlinks: [],
                            accessLevel: mediaData.access_level === 'paid' ? 'logged_in_required' : (mediaData.access_level || 'public'),
                            accessPassword: mediaData.access_password || '',
                            accessHint: mediaData.access_hint || '',
                            isPaid: mediaData.access_level === 'paid' || parseFloat(mediaData.price) > 0,
                            price: mediaData.price || '',
                            currency: mediaData.currency || 'USD',
                            isCopyrighted: !!mediaData.is_copyrighted,
                            copyrightText: mediaData.copyright_text || ''
                        });
                        
                        // Load current contributing artists for editing
                        try {
                            const contributorsResponse = await fetch(`/api/contributing-artists/upload/${mediaData.id}`, {
                                headers: {
                                    'Authorization': `Bearer ${localStorage.getItem('deepwave_token')}`
                                }
                            });
                            if (contributorsResponse.ok) {
                                const contributorsData = await contributorsResponse.json();
                                if (contributorsData.success) {
                                    setEditFormData(prev => ({
                                        ...prev,
                                        contributingArtists: contributorsData.data || []
                                    }));
                                }
                            }
                        } catch (error) {
                            console.error('Error loading contributing artists:', error);
                        }

                        // Track page view
                        trackingService.trackPageView('media', mediaData.id);

                        // Load media statistics
                        const stats = await trackingService.getMediaStats(mediaData.id);
                        if (stats && stats.success) {
                            setMediaStats(stats.data);
                        }


                        // Load comments
                        try {
                            setLoadingComments(true);
                            const commentsResponse = await api.get(`/upload/${mediaData.id}/comments`);
                            
                            if (commentsResponse.data.success) {
                                setComments(commentsResponse.data.data);
                            }
                        } catch (error) {
                            console.error('Error fetching comments:', error);
                        } finally {
                            setLoadingComments(false);
                        }

                        // Load hyperlinks
                        try {
                            setLoadingHyperlinks(true);
                            const hyperlinksResponse = await api.get(`/upload/${mediaData.id}/hyperlinks`);
                            
                            if (hyperlinksResponse.data.success) {
                                setHyperlinks(hyperlinksResponse.data.data);
                                // Update edit form data with hyperlinks
                                setEditFormData(prev => ({
                                    ...prev,
                                    hyperlinks: hyperlinksResponse.data.data
                                }));
                            }
                        } catch (error) {
                            console.error('Error fetching hyperlinks:', error);
                        } finally {
                            setLoadingHyperlinks(false);
                        }

                }
            } catch (err) {
                console.error('Error fetching media:', err);
                if (err.response?.status === 403) {
                    setError('This media is private.');
                } else if (err.response?.status === 404) {
                    setError('Media not found.');
                } else {
                    setError(`Failed to load media: ${err.message}`);
                }
            } finally {
                setLoading(false);
            }
        };

        if (type && username && filename) {
            fetchMediaMetadata();
        }
    }, [type, username, filename, isAuthenticated]);

    // VideoObject structured data — tells Google this page's primary content
    // is this specific video (Search Console's Video indexing report otherwise
    // classifies pages like this as "supplementary content" rather than a watch page).
    useEffect(() => {
        const scriptId = 'video-structured-data';
        const existing = document.getElementById(scriptId);

        if (media && media.type === 'video' && media.file_url) {
            const toIsoDuration = (seconds) => {
                const total = Math.round(Number(seconds));
                if (!total || total <= 0) return undefined;
                const h = Math.floor(total / 3600);
                const m = Math.floor((total % 3600) / 60);
                const s = total % 60;
                return `PT${h ? `${h}H` : ''}${m ? `${m}M` : ''}${s ? `${s}S` : ''}`;
            };

            const creator = media.display_name || media.username || 'The Deep Wave';
            const structuredData = {
                '@context': 'https://schema.org',
                '@type': 'VideoObject',
                name: media.title || 'Video',
                description: media.description || `Watch "${media.title || 'this video'}" by ${creator} on The Deep Wave`,
                thumbnailUrl: media.cover_image_url ? [media.cover_image_url] : undefined,
                uploadDate: media.created_at ? new Date(media.created_at).toISOString() : undefined,
                duration: toIsoDuration(media.duration),
                contentUrl: media.file_url,
                embedUrl: window.location.href,
                publisher: {
                    '@type': 'Organization',
                    name: 'The Deep Wave'
                }
            };

            let script = existing;
            if (!script) {
                script = document.createElement('script');
                script.id = scriptId;
                script.type = 'application/ld+json';
                document.head.appendChild(script);
            }
            script.textContent = JSON.stringify(structuredData);
        } else if (existing) {
            existing.remove();
        }

        return () => {
            const tag = document.getElementById(scriptId);
            if (tag) tag.remove();
        };
    }, [media]);

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (!passwordInput.trim()) return;

        setPasswordLoading(true);
        setPasswordError('');

        try {
            const response = await api.post(`/upload/${media.id}/verify-password`, {
                password: passwordInput
            });

            if (response.data.success) {
                let fileUrl = response.data.file_url;
                if (fileUrl && !fileUrl.startsWith('http')) {
                    fileUrl = window.location.origin + (fileUrl.startsWith('/') ? '' : '/') + fileUrl;
                }
                if (fileUrl && fileUrl.startsWith('http://') && window.location.protocol === 'https:') {
                    fileUrl = fileUrl.replace('http://', 'https://');
                }
                setMedia(prev => ({ ...prev, file_url: fileUrl }));
                setAccessRequired(false);
                setPasswordInput('');
            }
        } catch (err) {
            if (err.response?.status === 401) {
                setPasswordError('Incorrect password. Please try again.');
            } else {
                setPasswordError('Something went wrong. Please try again.');
            }
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleShare = () => {
        setShareModal({ isOpen: true, media });
    };

    const handleDownloadClick = () => {
        setDownloadPaywall({ isOpen: true, media });
    };

    const handleLike = async () => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: media?.type || 'audio' });
            return;
        }

        try {
            const response = await api.post(`/upload/${media.id}/like`);
            
            if (response.data.success) {
                setMedia(prev => ({
                    ...prev,
                    like_count: response.data.data.likeCount,
                    user_has_liked: response.data.data.liked
                }));
            }
        } catch (err) {
            console.error('Error toggling like:', err);
        }
    };

    const handleCommentLike = async (commentId) => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: media?.type || 'audio' });
            return;
        }

        try {
            const response = await api.post(`/upload/comments/${commentId}/like`);
            
            if (response.data.success) {
                setComments(prev => prev.map(comment => 
                    comment.id === commentId
                        ? { ...comment, like_count: response.data.data.likeCount, user_has_liked: response.data.data.liked }
                        : comment
                ));
            }
        } catch (err) {
            console.error('Error toggling comment like:', err);
        }
    };

    const handleAddComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim() || !isAuthenticated) return;

        try {
            setSubmittingComment(true);
            const response = await api.post(`/upload/${media.id}/comments`, {
                content: newComment.trim()
            });
            
            if (response.data.success) {
                setComments(prev => [response.data.data, ...prev]);
                setNewComment('');
                setMedia(prev => ({
                    ...prev,
                    comment_count: (prev.comment_count || 0) + 1
                }));
            }
        } catch (err) {
            console.error('Error adding comment:', err);
        } finally {
            setSubmittingComment(false);
        }
    };

    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);

        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
        if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)}w ago`;
        return date.toLocaleDateString();
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        // Reset form data
        const parsedTags = parseTagsSafely(media.tags);
        const tagsString = parsedTags.join(', ');
        
        setEditFormData({
            title: media.title || '',
            description: media.description || '',
            lyrics: media.lyrics || '',
            tags: tagsString,
            artist: media.artist || '',
            album: media.album || '',
            genre: media.genre ? media.genre.split(',').map(g => g.trim()) : [],
            year: media.year || '',
            bpm: media.bpm || '',
            key_signature: media.key_signature || '',
            track_number: media.track_number || '',
            duration: media.duration || '',
            hyperlinks: hyperlinks,
            accessLevel: media.access_level === 'paid' ? 'logged_in_required' : (media.access_level || 'public'),
            accessPassword: media.access_password || '',
            accessHint: media.access_hint || '',
            isPaid: media.access_level === 'paid' || parseFloat(media.price) > 0,
            price: media.price || '',
            currency: media.currency || 'USD',
            isCopyrighted: !!media.is_copyrighted,
            copyrightText: media.copyright_text || ''
        });
    };

    const handleSaveEdit = async () => {
        try {
            const tagsArray = editFormData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
            
            const response = await fetch(`/api/upload/${media.id}/metadata`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('deepwave_token')}`
                },
                body: JSON.stringify({
                    title: editFormData.title,
                    description: editFormData.description,
                    lyrics: editFormData.lyrics,
                    tags: tagsArray,
                    artist: editFormData.artist,
                    album: editFormData.album,
                    genre: Array.isArray(editFormData.genre) ? editFormData.genre.join(', ') : editFormData.genre,
                    year: editFormData.year ? parseInt(editFormData.year) : null,
                    bpm: editFormData.bpm ? parseInt(editFormData.bpm) : null,
                    key_signature: editFormData.key_signature,
                    track_number: editFormData.track_number ? parseInt(editFormData.track_number) : null,
                    duration: editFormData.duration ? parseInt(editFormData.duration) : null,
                    accessLevel: editFormData.accessLevel,
                    accessPassword: editFormData.accessPassword,
                    accessHint: editFormData.accessHint,
                    price: editFormData.isPaid ? editFormData.price : '',
                    currency: editFormData.currency,
                    isPublic: editFormData.accessLevel === 'public',
                    isCopyrighted: editFormData.isCopyrighted,
                    copyrightText: editFormData.isCopyrighted ? editFormData.copyrightText : ''
                })
            });

            if (response.ok) {
                // Save contributing artists (always, so removing the last one persists)
                if (editFormData.contributingArtists) {
                    try {
                        const artistIds = editFormData.contributingArtists.map(artist =>
                            artist.user_id || artist.artist_user_id
                        );
                        await fetch(`/api/contributing-artists/upload/${media.id}`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('deepwave_token')}`
                            },
                            body: JSON.stringify({
                                contributingArtistIds: artistIds
                            })
                        });
                    } catch (error) {
                        console.error('Error saving contributing artists:', error);
                    }
                }

                // Save hyperlinks if changed
                if (editFormData.hyperlinks) {
                    try {
                        await fetch(`/api/upload/${media.id}/hyperlinks`, {
                            method: 'PUT',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('deepwave_token')}`
                            },
                            body: JSON.stringify({
                                links: editFormData.hyperlinks
                            })
                        });
                        // Update local state
                        setHyperlinks(editFormData.hyperlinks);
                    } catch (error) {
                        console.error('Error saving hyperlinks:', error);
                    }
                }

                const updatedMedia = {
                    ...media,
                    title: editFormData.title,
                    description: editFormData.description,
                    lyrics: editFormData.lyrics,
                    tags: tagsArray.join(', '),
                    artist: editFormData.artist,
                    album: editFormData.album,
                    genre: Array.isArray(editFormData.genre) ? editFormData.genre.join(', ') : editFormData.genre,
                    year: editFormData.year ? parseInt(editFormData.year) : null,
                    bpm: editFormData.bpm ? parseInt(editFormData.bpm) : null,
                    key_signature: editFormData.key_signature,
                    track_number: editFormData.track_number ? parseInt(editFormData.track_number) : null,
                    duration: editFormData.duration ? parseInt(editFormData.duration) : null,
                    access_level: editFormData.accessLevel,
                    access_password: editFormData.accessPassword,
                    access_hint: editFormData.accessHint,
                    price: editFormData.isPaid ? editFormData.price : '',
                    currency: editFormData.currency,
                    is_public: editFormData.accessLevel === 'public'
                };
                setMedia(updatedMedia);
                setIsEditing(false);
            } else {
                console.error('Failed to update media');
            }
        } catch (err) {
            console.error('Error updating media:', err);
        }
    };

    const handleImageUpload = async (uploadData) => {
        try {
            setUploadingImage(true);

            // Update the media metadata to include the cover image URL
            const updateData = {
                cover_image_url: uploadData.optimizedUrl || uploadData.url
            };
            
            
            const response = await fetch(`/api/upload/${media.id}/metadata`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('deepwave_token')}`
                },
                body: JSON.stringify(updateData)
            });

            if (response.ok) {
                // Update the media object with the new cover image URL
                setMedia(prevMedia => ({
                    ...prevMedia,
                    cover_image_url: uploadData.optimizedUrl || uploadData.url
                }));
                setShowImageEditor(false);
            } else {
                console.error('Failed to update cover image');
                alert('Failed to update cover image. Please try again.');
            }
        } catch (err) {
            console.error('Error updating cover image:', err);
            alert('Error updating cover image. Please try again.');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleRemoveImage = async () => {
        try {
            setUploadingImage(true);

            // Update the media metadata to remove the cover image URL
            const updateData = {
                cover_image_url: null
            };
            
            
            const response = await fetch(`/api/upload/${media.id}/metadata`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('deepwave_token')}`
                },
                body: JSON.stringify(updateData)
            });

            if (response.ok) {
                // Update the media object to remove the cover image URL
                setMedia(prevMedia => ({
                    ...prevMedia,
                    cover_image_url: null
                }));
                setShowImageEditor(false);
            } else {
                console.error('Failed to remove cover image');
                alert('Failed to remove cover image. Please try again.');
            }
        } catch (err) {
            console.error('Error removing cover image:', err);
            alert('Error removing cover image. Please try again.');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleDelete = async () => {
        try {
            const response = await fetch(`/api/upload/${media.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('deepwave_token')}`
                }
            });

            if (response.ok) {
                // Redirect to profile page after successful deletion
                window.location.href = `/profile/${media.username}`;
            } else {
                console.error('Failed to delete media');
                alert('Failed to delete media. Please try again.');
            }
        } catch (err) {
            console.error('Error deleting media:', err);
            alert('Error deleting media. Please try again.');
        }
    };

    const isOwner = user && media && user.profile?.username === media.username;

    // Color schemes
    const colorSchemes = {
        wave: ['#9333ea', '#ec4899', '#06b6d4', '#8b5cf6'],
        fire: ['#ff0000', '#ff4500', '#ffa500', '#ffff00'],
        ocean: ['#0066cc', '#0080ff', '#00bfff', '#87ceeb'],
        neon: ['#ff073a', '#39ff14', '#ff073a', '#bf00ff'],
        sunset: ['#ff6b35', '#f7931e', '#ffd23f', '#06ffa5'],
        matrix: ['#00ff00', '#32cd32', '#00ff00', '#90ee90'],
        galaxy: ['#4c1d95', '#7c3aed', '#a855f7', '#d946ef']
    };

    // Visualizer styles
    const visualizerStyles = [
        { id: 'bars', name: 'Classic Bars', icon: BarChart3 },
        { id: 'wave', name: 'Sine Wave', icon: Waves },
        { id: 'circular', name: 'Circular', icon: Radio },
        { id: 'particles', name: 'Particles', icon: Zap },
        { id: 'oscilloscope', name: 'Oscilloscope', icon: Activity },
        { id: 'spectrum', name: 'Spectrum', icon: Radio },
        { id: 'dna', name: 'DNA Helix', icon: Activity }
    ];

    // Initialize audio visualizer
    const initAudioVisualizer = () => {
        if (!audioRef.current || audioContextRef.current) return;

        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const analyser = audioContext.createAnalyser();
            const source = audioContext.createMediaElementSource(audioRef.current);
            
            analyser.fftSize = visualizerSettings.fftSize;
            analyser.smoothingTimeConstant = visualizerSettings.smoothing;
            
            const bufferLength = analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);
            const timeDataArray = new Uint8Array(bufferLength);

            source.connect(analyser);
            analyser.connect(audioContext.destination);

            audioContextRef.current = audioContext;
            analyserRef.current = { analyser, dataArray, timeDataArray, bufferLength };

            startVisualization();
        } catch (error) {
            console.error('Error setting up audio visualizer:', error);
        }
    };

    // Get colors for current scheme
    const getColors = () => {
        return colorSchemes[visualizerSettings.colorScheme] || colorSchemes.wave;
    };

    // Apply bass boost to frequency data
    const applyBassBoost = (dataArray) => {
        const boost = visualizerSettings.bassBoost;
        const bassRange = Math.floor(dataArray.length * 0.1); // First 10% for bass
        
        for (let i = 0; i < bassRange; i++) {
            dataArray[i] = Math.min(255, dataArray[i] * boost);
        }
        return dataArray;
    };

    // Visualization functions for different styles
    const drawBars = (ctx, canvas, dataArray) => {
        const colors = getColors();
        const barWidth = canvas.width / dataArray.length;
        const sensitivity = visualizerSettings.sensitivity;

        for (let i = 0; i < dataArray.length; i++) {
            let barHeight = (dataArray[i] / 255) * canvas.height * 0.9 * sensitivity;
            
            if (visualizerSettings.mirrorMode) {
                barHeight *= 0.5;
            }

            const gradient = ctx.createLinearGradient(0, canvas.height - barHeight, 0, canvas.height);
            const colorIndex = Math.floor((i / dataArray.length) * colors.length);
            gradient.addColorStop(0, colors[colorIndex] + '80');
            gradient.addColorStop(1, colors[(colorIndex + 1) % colors.length]);

            ctx.fillStyle = gradient;
            
            if (visualizerSettings.mirrorMode) {
                // Draw bottom half
                ctx.fillRect(i * barWidth, canvas.height / 2, barWidth * 0.8, barHeight);
                // Draw top half (mirrored)
                ctx.fillRect(i * barWidth, canvas.height / 2 - barHeight, barWidth * 0.8, barHeight);
            } else {
                ctx.fillRect(i * barWidth, canvas.height - barHeight, barWidth * 0.8, barHeight);
            }
        }
    };

    const drawWave = (ctx, canvas, timeDataArray) => {
        const colors = getColors();
        ctx.strokeStyle = colors[0];
        ctx.lineWidth = visualizerSettings.lineThickness;
        ctx.beginPath();

        const sliceWidth = canvas.width / timeDataArray.length;
        let x = 0;

        for (let i = 0; i < timeDataArray.length; i++) {
            const v = (timeDataArray[i] / 128.0) * visualizerSettings.sensitivity;
            const y = v * canvas.height / 2;

            if (i === 0) {
                ctx.moveTo(x, y);
            } else {
                ctx.lineTo(x, y);
            }

            x += sliceWidth;
        }

        ctx.stroke();

        if (visualizerSettings.mirrorMode) {
            ctx.scale(1, -1);
            ctx.translate(0, -canvas.height);
            ctx.stroke();
            ctx.resetTransform();
        }
    };

    const drawCircular = (ctx, canvas, dataArray) => {
        const colors = getColors();
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        const radius = Math.min(centerX, centerY) * 0.8;

        for (let i = 0; i < dataArray.length; i++) {
            const angle = (i / dataArray.length) * Math.PI * 2;
            const amp = (dataArray[i] / 255) * radius * visualizerSettings.sensitivity;
            
            const x1 = centerX + Math.cos(angle) * radius;
            const y1 = centerY + Math.sin(angle) * radius;
            const x2 = centerX + Math.cos(angle) * (radius + amp);
            const y2 = centerY + Math.sin(angle) * (radius + amp);

            const colorIndex = Math.floor((i / dataArray.length) * colors.length);
            ctx.strokeStyle = colors[colorIndex];
            ctx.lineWidth = visualizerSettings.lineThickness;
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();
        }
    };

    const drawParticles = (ctx, canvas, dataArray) => {
        const colors = getColors();
        const particleCount = Math.min(visualizerSettings.particleCount, dataArray.length);

        for (let i = 0; i < particleCount; i++) {
            const amp = dataArray[Math.floor(i * dataArray.length / particleCount)] / 255;
            const x = (i / particleCount) * canvas.width;
            const y = canvas.height / 2 + (Math.sin(Date.now() * 0.001 + i) * amp * canvas.height * 0.3);
            const size = amp * 10 * visualizerSettings.sensitivity;

            const colorIndex = Math.floor((i / particleCount) * colors.length);
            ctx.fillStyle = colors[colorIndex];
            ctx.beginPath();
            ctx.arc(x, y, size, 0, Math.PI * 2);
            ctx.fill();

            if (visualizerSettings.glowIntensity > 0) {
                ctx.shadowColor = colors[colorIndex];
                ctx.shadowBlur = size * visualizerSettings.glowIntensity;
                ctx.fill();
            }
        }
    };

    const drawOscilloscope = (ctx, canvas, timeDataArray) => {
        const colors = getColors();
        ctx.strokeStyle = colors[0];
        ctx.lineWidth = visualizerSettings.lineThickness;
        
        // Draw grid
        ctx.strokeStyle = colors[1] + '40';
        ctx.lineWidth = 1;
        for (let i = 0; i <= 10; i++) {
            const y = (i / 10) * canvas.height;
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(canvas.width, y);
            ctx.stroke();
        }

        // Draw waveform
        ctx.strokeStyle = colors[0];
        ctx.lineWidth = visualizerSettings.lineThickness;
        ctx.beginPath();

        const sliceWidth = canvas.width / timeDataArray.length;
        let x = 0;

        for (let i = 0; i < timeDataArray.length; i++) {
            const v = (timeDataArray[i] / 128.0 - 1) * visualizerSettings.sensitivity;
            const y = canvas.height / 2 + v * canvas.height / 4;

            if (i === 0) {
                ctx.moveTo(x, y);
            } else {
                ctx.lineTo(x, y);
            }

            x += sliceWidth;
        }

        ctx.stroke();
    };

    const drawSpectrum = (ctx, canvas, dataArray) => {
        const colors = getColors();
        const barCount = Math.min(64, dataArray.length);
        const barWidth = canvas.width / barCount;

        for (let i = 0; i < barCount; i++) {
            const dataIndex = Math.floor(i * dataArray.length / barCount);
            const amp = dataArray[dataIndex] / 255;
            const hue = (i / barCount) * 360;
            
            const gradient = ctx.createLinearGradient(0, canvas.height, 0, 0);
            gradient.addColorStop(0, `hsl(${hue}, 70%, 40%)`);
            gradient.addColorStop(0.5, `hsl(${hue}, 80%, 60%)`);
            gradient.addColorStop(1, `hsl(${hue}, 90%, 80%)`);

            ctx.fillStyle = gradient;
            const barHeight = amp * canvas.height * visualizerSettings.sensitivity;
            ctx.fillRect(i * barWidth, canvas.height - barHeight, barWidth * 0.9, barHeight);
        }
    };

    const drawDNA = (ctx, canvas, dataArray) => {
        const colors = getColors();
        const centerY = canvas.height / 2;
        const amplitude = canvas.height * 0.3;

        ctx.lineWidth = visualizerSettings.lineThickness;

        for (let x = 0; x < canvas.width; x += 4) {
            const dataIndex = Math.floor((x / canvas.width) * dataArray.length);
            const amp = (dataArray[dataIndex] / 255) * visualizerSettings.sensitivity;
            
            const helixY1 = centerY + Math.sin((x / canvas.width) * Math.PI * 8) * amplitude * amp;
            const helixY2 = centerY - Math.sin((x / canvas.width) * Math.PI * 8) * amplitude * amp;

            ctx.strokeStyle = colors[0];
            ctx.beginPath();
            ctx.arc(x, helixY1, 2, 0, Math.PI * 2);
            ctx.stroke();

            ctx.strokeStyle = colors[1];
            ctx.beginPath();
            ctx.arc(x, helixY2, 2, 0, Math.PI * 2);
            ctx.stroke();

            if (x % 20 === 0) {
                ctx.strokeStyle = colors[2] + '60';
                ctx.beginPath();
                ctx.moveTo(x, helixY1);
                ctx.lineTo(x, helixY2);
                ctx.stroke();
            }
        }
    };

    // Main visualization loop
    const startVisualization = () => {
        if (!canvasRef.current || !analyserRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const { analyser, dataArray, timeDataArray, bufferLength } = analyserRef.current;

        const draw = () => {
            if (!analyser || !canvas) return;

            // Update canvas size
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;

            // Get audio data
            analyser.getByteFrequencyData(dataArray);
            analyser.getByteTimeDomainData(timeDataArray);

            // Apply bass boost
            applyBassBoost(dataArray);

            // Clear canvas with gradient background
            const colors = getColors();
            const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
            gradient.addColorStop(0, colors[0] + '10');
            gradient.addColorStop(1, colors[1] + '10');
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Reset shadow
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;

            // Apply glow effect
            if (visualizerSettings.glowIntensity > 0) {
                ctx.shadowBlur = 20 * visualizerSettings.glowIntensity;
            }

            // Draw based on selected style
            switch (visualizerStyle) {
                case 'bars':
                    drawBars(ctx, canvas, dataArray);
                    break;
                case 'wave':
                    drawWave(ctx, canvas, timeDataArray);
                    break;
                case 'circular':
                    drawCircular(ctx, canvas, dataArray);
                    break;
                case 'particles':
                    drawParticles(ctx, canvas, dataArray);
                    break;
                case 'oscilloscope':
                    drawOscilloscope(ctx, canvas, timeDataArray);
                    break;
                case 'spectrum':
                    drawSpectrum(ctx, canvas, dataArray);
                    break;
                case 'dna':
                    drawDNA(ctx, canvas, dataArray);
                    break;
                default:
                    drawBars(ctx, canvas, dataArray);
            }

            animationRef.current = requestAnimationFrame(draw);
        };

        draw();
    };

    // Cleanup visualizer
    const cleanupVisualizer = () => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            audioContextRef.current.close();
        }
        audioContextRef.current = null;
        analyserRef.current = null;
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupVisualizer();
        };
    }, []);

    // Stop play tracking if the user navigates away mid-playback (SPA route
    // change doesn't trigger the audio's own 'pause'/'ended' listeners).
    useEffect(() => {
        return () => {
            if (media?.id) {
                trackingService.stopPlayTracking(media.id);
            }
        };
    }, [media?.id]);

    // Handle audio play/pause for visualizer
    const handleAudioPlay = () => {
        if (audioRef.current) {
            trackingService.startPlayTracking(media.id, audioRef.current);

            // Initialize visualizer on first play
            if (!audioContextRef.current) {
                initAudioVisualizer();
            } else if (audioContextRef.current.state === 'suspended') {
                audioContextRef.current.resume();
            }
        }
        setIsAudioPlaying(true);
    };

    const handleAudioPause = () => {
        setIsAudioPlaying(false);
        // Don't stop tracking on natural end-of-track — the tracking service's
        // own 'ended' listener already finalizes and cleans that case up itself.
        if (audioRef.current && !audioRef.current.ended) {
            trackingService.stopPlayTracking(media.id);
        }
    };

    const toggleAudioPlayback = () => {
        if (!audioRef.current) return;
        if (audioRef.current.paused) {
            audioRef.current.play();
        } else {
            audioRef.current.pause();
        }
    };

    // Every audio play button funnels through here so gated tracks show the
    // login/support popup instead of actually starting playback.
    const handlePlayClick = () => {
        if (accessRequired) {
            setShowAccessModal(true);
        } else {
            toggleAudioPlayback();
        }
    };

    const handleAudioTimeUpdate = () => {
        if (audioRef.current) {
            setAudioCurrentTime(audioRef.current.currentTime);
        }
    };

    const handleAudioLoadedMetadata = () => {
        if (audioRef.current && Number.isFinite(audioRef.current.duration)) {
            setAudioDuration(audioRef.current.duration);
        }
    };

    const handleSeek = (e) => {
        if (!audioRef.current || !audioDuration) return;
        const bar = e.currentTarget;
        const rect = bar.getBoundingClientRect();
        const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
        audioRef.current.currentTime = ratio * audioDuration;
        setAudioCurrentTime(ratio * audioDuration);
    };

    const formatPlaybackTime = (seconds) => {
        if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-white text-lg">Loading media...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-red-400 text-lg mb-4">{error}</div>
                    <a href="/" className="text-purple-400 hover:text-purple-300">
                        Go back to home
                    </a>
                </div>
            </div>
        );
    }

    const renderMedia = () => {
        switch (media.type) {
            case 'video':
                if (accessRequired) {
                    // No native controls exposed while gated — a transparent
                    // overlay intercepts the click and shows the login/support
                    // popup instead of letting the video actually play.
                    return (
                        <div className="relative">
                            <video
                                src={media.file_url}
                                poster={media.cover_image_url}
                                className="w-full max-h-[70vh] bg-black rounded-lg shadow-2xl pointer-events-none"
                                preload="metadata"
                                playsInline
                            />
                            <button
                                type="button"
                                onClick={() => setShowAccessModal(true)}
                                aria-label="Protected content"
                                className="absolute inset-0 flex items-center justify-center group"
                            >
                                <div className="w-16 h-16 bg-white/20 group-hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors">
                                    <Play className="w-7 h-7 text-white ml-1" />
                                </div>
                            </button>
                        </div>
                    );
                }
                return (
                    <video
                        ref={videoRef}
                        src={media.file_url}
                        className="w-full max-h-[70vh] bg-black rounded-lg shadow-2xl"
                        controls
                        controlsList="nodownload"
                        preload="metadata"
                        playsInline
                        onPlay={() => {
                            if (videoRef.current) {
                                trackingService.startPlayTracking(media.id, videoRef.current);
                            }
                        }}
                        onPause={() => {
                            // Don't stop tracking on natural end-of-video — the tracking
                            // service's own 'ended' listener finalizes that case itself.
                            if (videoRef.current && !videoRef.current.ended) {
                                trackingService.stopPlayTracking(media.id);
                            }
                        }}
                        onLoadedMetadata={() => {
                            // Additional tracking setup if needed
                        }}
                    />
                );
            case 'audio':
                return (
                    <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg p-6 text-center">
                        {/* Album Cover with Play Bar */}
                        <div className="bg-gray-900/40 backdrop-blur-sm rounded-lg p-4 max-w-md lg:max-w-2xl mx-auto mb-6">
                            {/* Album Cover */}
                            {media.cover_image_url && (
                                <div className="relative group mb-4">
                                    <img
                                        src={media.cover_image_url}
                                        alt={`${media.title} cover art`}
                                        className="w-full aspect-square object-cover rounded-lg shadow-lg"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                                    {/* Edit Image Button - Only show for owner */}
                                    {isOwner && (
                                        <button
                                            onClick={() => setShowImageEditor(true)}
                                            className="absolute top-3 right-3 p-2 bg-gray-900/80 hover:bg-gray-800/90 rounded-lg transition-colors backdrop-blur-sm opacity-0 group-hover:opacity-100"
                                            title="Edit cover image"
                                        >
                                            <Edit3 className="w-4 h-4 text-white" />
                                        </button>
                                    )}

                                    {/* Track Info Overlay */}
                                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <h3 className="font-bold text-lg leading-tight mb-1">{media.title}</h3>
                                        <p className="text-sm text-gray-300">by {media.display_name || media.username}</p>
                                        {mediaStats && (
                                            <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                                                {/* Only show Plays for audio and video, not images */}
                                                {(media?.type === 'audio' || media?.type === 'video') && (
                                                    <span className="flex items-center gap-1">
                                                        <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                                                        {mediaStats.playCount.toLocaleString()} plays
                                                    </span>
                                                )}
                                                <span className="flex items-center gap-1">
                                                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                                                    {mediaStats.viewCount.toLocaleString()} views
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Fallback when no cover art */}
                            {!media.cover_image_url && (
                                <div className="relative w-full aspect-square bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex flex-col items-center justify-center text-gray-400 mb-4">
                                    <div className="text-6xl mb-3">🎵</div>
                                    <h3 className="font-bold text-lg text-white mb-1">{media.title}</h3>
                                    <p className="text-sm">by {media.display_name || media.username}</p>
                                    {mediaStats && (
                                        <div className="flex items-center gap-3 mt-3 text-xs">
                                            {/* Only show Plays for audio and video, not images */}
                                            {(media?.type === 'audio' || media?.type === 'video') && (
                                                <span className="flex items-center gap-1">
                                                    <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                                                    {mediaStats.playCount.toLocaleString()} plays
                                                </span>
                                            )}
                                            <span className="flex items-center gap-1">
                                                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                                                {mediaStats.viewCount.toLocaleString()} views
                                            </span>
                                        </div>
                                    )}

                                    {/* Add Cover Art Button - Only show for owner */}
                                    {isOwner && (
                                        <button
                                            onClick={() => setShowImageEditor(true)}
                                            className="absolute top-3 right-3 p-2 bg-gray-800/80 hover:bg-gray-700/90 rounded-lg transition-colors backdrop-blur-sm flex items-center gap-2"
                                            title="Add cover image"
                                        >
                                            <Upload className="w-4 h-4 text-purple-400" />
                                            <span className="text-xs text-purple-400 hidden sm:block">Add Cover</span>
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Play bar */}
                            <audio
                                ref={audioRef}
                                src={media.file_url}
                                className="hidden"
                                preload="metadata"
                                crossOrigin="anonymous"
                                onPlay={handleAudioPlay}
                                onPause={handleAudioPause}
                                onTimeUpdate={handleAudioTimeUpdate}
                                onLoadedMetadata={handleAudioLoadedMetadata}
                            />
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handlePlayClick}
                                    aria-label={isAudioPlaying ? 'Pause' : 'Play'}
                                    className="flex-shrink-0 w-10 h-10 rounded-full bg-white flex items-center justify-center hover:scale-105 transition-transform"
                                >
                                    {isAudioPlaying ? (
                                        <Pause className="w-4 h-4 text-purple-700 fill-current" />
                                    ) : (
                                        <Play className="w-4 h-4 text-purple-700 fill-current ml-0.5" />
                                    )}
                                </button>
                                <span className="text-xs text-gray-300 w-10 text-right flex-shrink-0">
                                    {formatPlaybackTime(audioCurrentTime)}
                                </span>
                                <div
                                    onClick={handleSeek}
                                    className="relative flex-1 h-1.5 bg-white/20 rounded-full cursor-pointer group/bar"
                                >
                                    <div
                                        className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full"
                                        style={{ width: `${audioDuration ? (audioCurrentTime / audioDuration) * 100 : 0}%` }}
                                    />
                                    <div
                                        className="absolute top-1/2 w-3 h-3 bg-white rounded-full shadow -translate-y-1/2 -translate-x-1/2 opacity-0 group-hover/bar:opacity-100 transition-opacity"
                                        style={{ left: `${audioDuration ? (audioCurrentTime / audioDuration) * 100 : 0}%` }}
                                    />
                                </div>
                                <span className="text-xs text-gray-300 w-10 flex-shrink-0">
                                    {formatPlaybackTime(audioDuration)}
                                </span>
                            </div>
                        </div>

                        {/* Audio Visualizer */}
                        <div className="mb-6">
                            <div className="bg-gray-900/30 backdrop-blur-sm rounded-lg p-4 mb-4 relative">
                                {/* Visualizer Controls */}
                                <div className="absolute top-2 right-2 z-10 flex gap-2">
                                    {/* Style Selector */}
                                    <div className="relative">
                                        <button
                                            onClick={() => setShowVisualizerOptions(!showVisualizerOptions)}
                                            className="bg-gray-800/80 hover:bg-gray-700/80 text-white p-2 rounded-lg transition-all duration-200 backdrop-blur-sm"
                                            title="Visualizer Options"
                                        >
                                            <Settings className="w-4 h-4" />
                                        </button>

                                        {showVisualizerOptions && (
                                            <div className="absolute top-12 right-0 bg-gray-900/95 backdrop-blur-lg border border-gray-700 rounded-lg p-4 w-80 max-h-96 overflow-y-auto z-20">
                                                {/* Style Selection */}
                                                <div className="mb-4">
                                                    <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                                                        <Palette className="w-4 h-4" />
                                                        Visualizer Style
                                                    </h3>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        {visualizerStyles.map((style) => {
                                                            const IconComponent = style.icon;
                                                            return (
                                                                <button
                                                                    key={style.id}
                                                                    onClick={() => setVisualizerStyle(style.id)}
                                                                    className={`p-3 rounded-lg text-sm transition-all duration-200 flex items-center gap-2 ${
                                                                        visualizerStyle === style.id
                                                                            ? 'bg-purple-600 text-white'
                                                                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                                                    }`}
                                                                >
                                                                    <IconComponent className="w-4 h-4" />
                                                                    <span className="text-xs">{style.name}</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                {/* Color Schemes */}
                                                <div className="mb-4">
                                                    <h3 className="text-sm font-medium text-white mb-3">Color Scheme</h3>
                                                    <div className="grid grid-cols-3 gap-2">
                                                        {Object.keys(colorSchemes).map((scheme) => (
                                                            <button
                                                                key={scheme}
                                                                onClick={() => setVisualizerSettings({...visualizerSettings, colorScheme: scheme})}
                                                                className={`p-2 rounded-lg text-xs capitalize transition-all duration-200 ${
                                                                    visualizerSettings.colorScheme === scheme
                                                                        ? 'bg-purple-600 text-white'
                                                                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                                                }`}
                                                            >
                                                                <div className="flex gap-1 mb-1">
                                                                    {colorSchemes[scheme].slice(0, 3).map((color, i) => (
                                                                        <div key={i} className="w-3 h-3 rounded-full" style={{backgroundColor: color}}></div>
                                                                    ))}
                                                                </div>
                                                                {scheme}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Settings Sliders */}
                                                <div className="space-y-3">
                                                    <div>
                                                        <label className="text-xs text-gray-300 mb-1 block">Sensitivity: {visualizerSettings.sensitivity.toFixed(1)}</label>
                                                        <input
                                                            type="range"
                                                            min="0.1"
                                                            max="3"
                                                            step="0.1"
                                                            value={visualizerSettings.sensitivity}
                                                            onChange={(e) => setVisualizerSettings({...visualizerSettings, sensitivity: parseFloat(e.target.value)})}
                                                            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="text-xs text-gray-300 mb-1 block">Smoothing: {visualizerSettings.smoothing.toFixed(1)}</label>
                                                        <input
                                                            type="range"
                                                            min="0"
                                                            max="1"
                                                            step="0.1"
                                                            value={visualizerSettings.smoothing}
                                                            onChange={(e) => setVisualizerSettings({...visualizerSettings, smoothing: parseFloat(e.target.value)})}
                                                            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="text-xs text-gray-300 mb-1 block">Glow: {visualizerSettings.glowIntensity.toFixed(1)}</label>
                                                        <input
                                                            type="range"
                                                            min="0"
                                                            max="2"
                                                            step="0.1"
                                                            value={visualizerSettings.glowIntensity}
                                                            onChange={(e) => setVisualizerSettings({...visualizerSettings, glowIntensity: parseFloat(e.target.value)})}
                                                            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="text-xs text-gray-300 mb-1 block">Bass Boost: {visualizerSettings.bassBoost.toFixed(1)}</label>
                                                        <input
                                                            type="range"
                                                            min="0.5"
                                                            max="3"
                                                            step="0.1"
                                                            value={visualizerSettings.bassBoost}
                                                            onChange={(e) => setVisualizerSettings({...visualizerSettings, bassBoost: parseFloat(e.target.value)})}
                                                            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="text-xs text-gray-300 mb-1 block">Line Thickness: {visualizerSettings.lineThickness}</label>
                                                        <input
                                                            type="range"
                                                            min="1"
                                                            max="5"
                                                            step="1"
                                                            value={visualizerSettings.lineThickness}
                                                            onChange={(e) => setVisualizerSettings({...visualizerSettings, lineThickness: parseInt(e.target.value)})}
                                                            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                                                        />
                                                    </div>

                                                    {visualizerStyle === 'particles' && (
                                                        <div>
                                                            <label className="text-xs text-gray-300 mb-1 block">Particles: {visualizerSettings.particleCount}</label>
                                                            <input
                                                                type="range"
                                                                min="10"
                                                                max="100"
                                                                step="5"
                                                                value={visualizerSettings.particleCount}
                                                                onChange={(e) => setVisualizerSettings({...visualizerSettings, particleCount: parseInt(e.target.value)})}
                                                                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                                                            />
                                                        </div>
                                                    )}

                                                    {/* Toggle Options */}
                                                    <div className="flex items-center justify-between py-1">
                                                        <label className="text-xs text-gray-300">Mirror Mode</label>
                                                        <button
                                                            onClick={() => setVisualizerSettings({...visualizerSettings, mirrorMode: !visualizerSettings.mirrorMode})}
                                                            className={`w-10 h-5 rounded-full transition-colors ${
                                                                visualizerSettings.mirrorMode ? 'bg-purple-600' : 'bg-gray-600'
                                                            }`}
                                                        >
                                                            <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                                                                visualizerSettings.mirrorMode ? 'translate-x-5' : 'translate-x-0.5'
                                                            }`}></div>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Canvas */}
                                <div className="relative">
                                    <canvas
                                        ref={canvasRef}
                                        className="w-full h-40 rounded-lg"
                                        style={{ imageRendering: 'crisp-edges' }}
                                    />
                                    <button
                                        onClick={handlePlayClick}
                                        aria-label={isAudioPlaying ? 'Pause' : 'Play'}
                                        className="absolute inset-0 flex items-center justify-center group"
                                    >
                                        {!isAudioPlaying && (
                                            <div className="w-16 h-16 bg-white/20 group-hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors">
                                                <Play className="w-7 h-7 text-white ml-1" />
                                            </div>
                                        )}
                                    </button>
                                </div>

                                {/* Status */}
                                <div className="flex items-center justify-between mt-2 text-white/80 text-sm">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                                        <span>Live Visualizer</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs capitalize">{visualizerStyle}</span>
                                        <span className="text-xs">•</span>
                                        <span className="text-xs capitalize">{visualizerSettings.colorScheme}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'image':
                return (
                    <div className="relative group" style={{ maxWidth: '98%', margin: '15px auto 0', display: 'block' }}>
                        <img
                            src={media.file_url}
                            alt={media.title}
                            className="rounded-lg shadow-2xl cursor-pointer select-none"
                            onClick={() => setIsImageExpanded(!isImageExpanded)}
                            onContextMenu={(e) => e.preventDefault()}
                            onDragStart={(e) => e.preventDefault()}
                            style={{ 
                                width: '100%',
                                maxHeight: '80vh',
                                objectFit: 'contain',
                                userSelect: 'none',
                                WebkitUserSelect: 'none',
                                MozUserSelect: 'none',
                                msUserSelect: 'none',
                                WebkitTouchCallout: 'none',
                                WebkitUserDrag: 'none'
                            }}
                        />
                        
                        {/* Magnifying Glass Icon Overlay */}
                        {!isImageExpanded && (
                            <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm rounded-full p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                                <ZoomIn className="w-6 h-6 text-white" />
                            </div>
                        )}
                    </div>
                );
            default:
                return null;
        }
    };

    const renderMediaBackground = () => {
        if (!media) return null;
        switch (media.type) {
            case 'video':
                return media.cover_image_url ? (
                    <img
                        src={media.cover_image_url}
                        alt={media.title}
                        className="w-full object-cover rounded-lg shadow-2xl"
                        style={{ minHeight: '320px', maxHeight: '70vh' }}
                    />
                ) : (
                    <div className="w-full bg-black rounded-lg shadow-2xl flex items-center justify-center" style={{ minHeight: '320px' }}>
                        <Image className="w-16 h-16 text-gray-600" />
                    </div>
                );
            case 'audio':
                return (
                    <div className="bg-gradient-to-br from-purple-900 to-pink-900 rounded-lg overflow-hidden flex items-center justify-center" style={{ minHeight: '320px' }}>
                        {media.cover_image_url ? (
                            <img src={media.cover_image_url} alt={media.title} className="w-full h-full object-cover" style={{ minHeight: '320px' }} />
                        ) : (
                            <Music className="w-24 h-24 text-white/20" />
                        )}
                    </div>
                );
            case 'image':
                return (
                    <div className="w-full rounded-lg overflow-hidden shadow-2xl" style={{ minHeight: '320px' }}>
                        {media.cover_image_url ? (
                            <img
                                src={media.cover_image_url}
                                alt={media.title}
                                className="w-full h-full object-contain"
                                style={{ maxHeight: '80vh' }}
                            />
                        ) : (
                            <div className="w-full h-full bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center" style={{ minHeight: '320px' }}>
                                <Image className="w-24 h-24 text-gray-600" />
                            </div>
                        )}
                    </div>
                );
            default:
                return null;
        }
    };

    // Private / password-protected / pure-purchase content has no working
    // small-badge equivalent (no login/subscribe action applies), so it keeps
    // the full lock screen. Login / subscription gates render normally with a
    // small locked indicator + gated play buttons, matching the rest of the site.
    const isHardGate = accessRequired &&
        (media?.access_type === 'private' || media?.access_type === 'password' || media?.access_type === 'payment' || media?.type === 'image');

    // AccessRequiredModal speaks the site-wide access_level vocabulary
    // (logged_in_required / subscription_required); this page's own gate
    // check uses a slightly different access_type vocabulary, so map it.
    const softGateAccessLevel = media?.access_type === 'login' ? 'logged_in_required'
        : (media?.access_type === 'subscription' || media?.access_type === 'subscription_or_payment') ? 'subscription_required'
        : null;
    const isSoftGated = accessRequired && !isHardGate;

    return (
        <div className="min-h-screen bg-gray-900 text-white">
            {/* Header */}
            <div className="bg-gray-800/50 backdrop-blur-sm border-b border-gray-700 px-0 sm:px-6 py-4">
                <div className="max-w-6xl mx-auto px-2 sm:px-0">
                    <div className="flex items-center flex-wrap gap-2 sm:gap-4">
                        <button
                            onClick={handleDownloadClick}
                            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                        >
                            <Download className="w-4 h-4" />
                            Download
                        </button>
                        <button
                            onClick={handleShare}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                        >
                            <Share2 className="w-4 h-4" />
                            Share
                        </button>
                        <button
                            onClick={handleLike}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                                media?.user_has_liked
                                    ? 'text-red-400 bg-red-400/10 hover:bg-red-400/20' 
                                    : 'text-gray-400 hover:text-red-400 hover:bg-red-400/10'
                            }`}
                            title={isAuthenticated 
                                ? (media?.user_has_liked ? `Unlike this ${media?.type}` : `Like this ${media?.type}`)
                                : `Sign up to like ${media?.type}s and support artists`
                            }
                        >
                            <Heart className={`w-4 h-4 ${media?.user_has_liked ? 'fill-current' : ''}`} />
                            {media?.like_count || 0}
                        </button>

                        {media?.type === 'audio' && (
                            <button
                                onClick={handlePlayClick}
                                aria-label={isAudioPlaying ? 'Pause' : 'Play'}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                            >
                                {isAudioPlaying ? (
                                    <Pause className="w-4 h-4" />
                                ) : (
                                    <Play className="w-4 h-4" />
                                )}
                                {isAudioPlaying ? 'Pause' : 'Play'}
                            </button>
                        )}

                        {isOwner && (
                            <button
                                onClick={isEditing ? handleSaveEdit : handleEdit}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                            >
                                {isEditing ? <Save className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
                                {isEditing ? 'Save' : 'Edit'}
                            </button>
                        )}
                        
                        {isEditing && (
                            <button
                                onClick={handleCancelEdit}
                                className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors"
                            >
                                <X className="w-4 h-4" />
                                Cancel
                            </button>
                        )}
                        
                        {isOwner && !isEditing && (
                            <button
                                onClick={() => setShowDeleteModal(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                            >
                                <Trash2 className="w-4 h-4" />
                                Delete
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-6xl mx-auto px-0 sm:px-6 py-6">
                {/* Media Player / Access Gate */}
                <div className="mb-8 relative">
                    {isHardGate ? (
                        /* Full width, matching the normal (unlocked) media layout — the
                           preview stays fully visible behind a small gate popup. */
                        <div className="relative">
                            {renderMediaBackground()}
                            {/* Gate overlay — a small popup over the full-size preview */}
                            <div className="absolute inset-0 flex items-center justify-center z-10 p-6 rounded-lg">
                                <div className="absolute inset-0 bg-black/25 rounded-lg" />
                                <div className="relative z-10 w-full max-w-md">
                                    {media?.access_type === 'private' ? (
                                        /* ── Private gate ── */
                                        <div className="bg-gray-900/95 border border-blue-700/40 rounded-2xl p-8 text-center shadow-2xl">
                                            <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-5">
                                                <Users className="w-8 h-8 text-blue-400" />
                                            </div>
                                            <h2 className="text-2xl font-bold text-white mb-3">Private Release</h2>
                                            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                                                This content is kept private by the creator and isn't available for public viewing.
                                            </p>
                                            <a
                                                href="/"
                                                className="block w-full py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors"
                                            >
                                                Back to Home
                                            </a>
                                        </div>
                                    ) : media?.access_type === 'payment' ? (
                                        /* ── Purchase gate ── */
                                        <div className="bg-gray-900/95 border border-green-700/40 rounded-2xl p-8 text-center shadow-2xl">
                                            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-5">
                                                <DollarSign className="w-8 h-8 text-green-400" />
                                            </div>
                                            <h2 className="text-2xl font-bold text-white mb-2">Support the Artist</h2>
                                            {media?.price && (
                                                <p className="text-3xl font-bold text-green-400 mb-2">
                                                    {media.currency || 'USD'} {parseFloat(media.price).toFixed(2)}
                                                </p>
                                            )}
                                            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                                                Unlock this exclusive content with a one-time purchase and directly support the artist's work.
                                            </p>
                                            {!isAuthenticated ? (
                                                <div className="flex flex-col gap-3">
                                                    <a
                                                        href={`/login?redirect=${encodeURIComponent(currentPagePath())}`}
                                                        className="block w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
                                                    >
                                                        Log In to Purchase
                                                    </a>
                                                    <a
                                                        href={`/register?redirect=${encodeURIComponent(currentPagePath())}`}
                                                        className="block w-full py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors"
                                                    >
                                                        Join for Free
                                                    </a>
                                                </div>
                                            ) : (
                                                <button className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors">
                                                    Unlock Now
                                                    {media?.price && (
                                                        <span className="ml-2 opacity-80">
                                                            — {media.currency || 'USD'} {parseFloat(media.price).toFixed(2)}
                                                        </span>
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    ) : media?.access_type === 'subscription_or_payment' ? (
                                        /* ── Subscribe OR buy gate ── */
                                        <div className="bg-gray-900/95 border border-purple-700/40 rounded-2xl p-8 text-center shadow-2xl">
                                            <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-5">
                                                <Star className="w-8 h-8 text-purple-400" />
                                            </div>
                                            <h2 className="text-2xl font-bold text-white mb-2">Exclusive Content</h2>
                                            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                                                Unlock by becoming a supporter or with a one-time purchase to support the artist.
                                            </p>
                                            {!isAuthenticated ? (
                                                <div className="flex flex-col gap-3">
                                                    <a
                                                        href={`/login?redirect=${encodeURIComponent(currentPagePath())}`}
                                                        className="block w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors"
                                                    >
                                                        Log In to Access
                                                    </a>
                                                    <a
                                                        href={`/register?redirect=${encodeURIComponent(currentPagePath())}`}
                                                        className="block w-full py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors"
                                                    >
                                                        Join for Free
                                                    </a>
                                                </div>
                                            ) : (
                                                <div className="flex flex-col gap-3">
                                                    <a
                                                        href="/subscriptions"
                                                        className="block w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors"
                                                    >
                                                        Become a Supporter
                                                    </a>
                                                    <div className="flex items-center gap-3 my-1">
                                                        <div className="flex-1 h-px bg-gray-700" />
                                                        <span className="text-gray-500 text-xs">or</span>
                                                        <div className="flex-1 h-px bg-gray-700" />
                                                    </div>
                                                    <button className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors">
                                                        Buy Once
                                                        {media?.price && (
                                                            <span className="ml-2 opacity-80">
                                                                — {media.currency || 'USD'} {parseFloat(media.price).toFixed(2)}
                                                            </span>
                                                        )}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ) : media?.access_type === 'login' || media?.access_level === 'logged_in_required' ? (
                                        /* ── Login gate ── */
                                        <div className="bg-gray-900/95 border border-cyan-700/40 rounded-2xl p-8 text-center shadow-2xl">
                                            <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-5">
                                                <LogIn className="w-8 h-8 text-cyan-400" />
                                            </div>
                                            <h2 className="text-2xl font-bold text-white mb-3">Requires Login</h2>
                                            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                                                Login to access this content. Show your support for our art by joining The Deep Wave community.
                                            </p>
                                            <div className="flex flex-col gap-3">
                                                <a
                                                    href={`/login?redirect=${encodeURIComponent(currentPagePath())}`}
                                                    className="block w-full py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition-colors"
                                                >
                                                    Log In Now
                                                </a>
                                                <a
                                                    href={`/register?redirect=${encodeURIComponent(currentPagePath())}`}
                                                    className="block w-full py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors"
                                                >
                                                    Join for Free
                                                </a>
                                            </div>
                                        </div>
                                    ) : media?.access_type === 'subscription' || media?.access_level === 'subscription_required' ? (
                                        /* ── Subscription gate ── */
                                        <div className="bg-gray-900/95 border border-purple-700/40 rounded-2xl p-8 text-center shadow-2xl">
                                            <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-5">
                                                <Star className="w-8 h-8 text-purple-400" />
                                            </div>
                                            <h2 className="text-2xl font-bold text-white mb-3">For Supporters</h2>
                                            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                                                This exclusive content is available to Deep Wave supporters. Join to unlock this and all supporter content and support the art.
                                            </p>
                                            <a
                                                href="/subscriptions"
                                                className="block w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors mb-3"
                                            >
                                                Become a Supporter
                                            </a>
                                            {isAuthenticated && (
                                                <p className="text-gray-500 text-xs">
                                                    Already a supporter?{' '}
                                                    <button onClick={() => window.location.reload()} className="text-purple-400 hover:text-purple-300 underline">
                                                        Refresh the page
                                                    </button>
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        /* ── Password gate ── */
                                        <div className="bg-gray-900/95 border border-yellow-700/40 rounded-2xl p-8 text-center shadow-2xl">
                                            <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-5">
                                                <svg className="w-8 h-8 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                </svg>
                                            </div>
                                            <h2 className="text-2xl font-bold text-white mb-3">Unlock with Password</h2>
                                            <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                                                Enter the password below to access this content.
                                            </p>
                                            {media?.access_hint && (
                                                <p className="text-yellow-300/80 text-sm mb-4 italic bg-yellow-500/10 rounded-lg px-3 py-2">
                                                    Hint: {media.access_hint}
                                                </p>
                                            )}
                                            <form onSubmit={handlePasswordSubmit} className="space-y-3">
                                                <input
                                                    type="password"
                                                    value={passwordInput}
                                                    onChange={(e) => { setPasswordInput(e.target.value); setPasswordError(''); }}
                                                    placeholder="Enter password"
                                                    className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                                                    autoFocus
                                                />
                                                {passwordError && (
                                                    <p className="text-red-400 text-sm text-left">{passwordError}</p>
                                                )}
                                                <button
                                                    type="submit"
                                                    disabled={!passwordInput.trim() || passwordLoading}
                                                    className="w-full py-3 bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 text-white font-semibold rounded-lg transition-colors"
                                                >
                                                    {passwordLoading ? 'Checking...' : 'Unlock Content'}
                                                </button>
                                            </form>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        renderMedia()
                    )}
                </div>

                {/* Media Info */}
                <div className="bg-gray-800/50 rounded-lg p-6">
                    {isEditing ? (
                        /* ── Full-width edit panel ── */
                        <div className="space-y-8">
                            {/* Edit header */}
                            <div className="flex items-center justify-between pb-4 border-b border-gray-700">
                                <div className="flex items-center gap-2">
                                    <Edit3 className="w-5 h-5 text-purple-400" />
                                    <span className="font-semibold text-white">Editing</span>
                                    <span className="text-gray-400 text-sm hidden sm:inline">— {media.title}</span>
                                </div>
                                <div className="flex gap-3">
                                    <button onClick={handleCancelEdit} className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors text-sm">
                                        <X className="w-4 h-4" /> Cancel
                                    </button>
                                    <button onClick={handleSaveEdit} className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white transition-colors text-sm font-semibold">
                                        <Save className="w-4 h-4" /> Save Changes
                                    </button>
                                </div>
                            </div>

                            {/* Section: Basic Info */}
                            <div>
                                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Basic Info</h3>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Title *</label>
                                        <input
                                            type="text"
                                            value={editFormData.title}
                                            onChange={(e) => setEditFormData({...editFormData, title: e.target.value})}
                                            className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                            placeholder="Enter title"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Tags</label>
                                        <input
                                            type="text"
                                            value={editFormData.tags}
                                            onChange={(e) => setEditFormData({...editFormData, tags: e.target.value})}
                                            className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                            placeholder="Separate with commas"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center gap-1 mb-2 border-b border-gray-700">
                                        <button
                                            type="button"
                                            onClick={() => setDescriptionTab('description')}
                                            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                                                descriptionTab === 'description'
                                                    ? 'text-purple-400 border-purple-400'
                                                    : 'text-gray-400 border-transparent hover:text-gray-300'
                                            }`}
                                        >
                                            Description
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setDescriptionTab('lyrics')}
                                            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                                                descriptionTab === 'lyrics'
                                                    ? 'text-purple-400 border-purple-400'
                                                    : 'text-gray-400 border-transparent hover:text-gray-300'
                                            }`}
                                        >
                                            Lyrics
                                        </button>
                                    </div>
                                    {descriptionTab === 'description' ? (
                                        <textarea
                                            value={editFormData.description}
                                            onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                                            className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-vertical"
                                            rows="4"
                                            placeholder="Describe this track, image, or video..."
                                        />
                                    ) : (
                                        <textarea
                                            value={editFormData.lyrics}
                                            onChange={(e) => setEditFormData({...editFormData, lyrics: e.target.value})}
                                            className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-vertical"
                                            rows="4"
                                            placeholder="Add the full lyrics here..."
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Section: Cover Image (audio/video only — reuses the same
                                showImageEditor modal + handleImageUpload/handleRemoveImage
                                logic as the hover button on the artwork itself). */}
                            {(media.type === 'audio' || media.type === 'video') && (
                                <div>
                                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Cover Image</h3>
                                    <div className="flex items-center gap-4">
                                        <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-800 border border-gray-700 flex-shrink-0 flex items-center justify-center">
                                            {media.cover_image_url ? (
                                                <img src={media.cover_image_url} alt="Cover" className="w-full h-full object-cover" />
                                            ) : (
                                                <Image className="w-6 h-6 text-gray-500" />
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowImageEditor(true)}
                                            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm transition-colors"
                                        >
                                            <Upload className="w-4 h-4" />
                                            {media.cover_image_url ? 'Change Image' : 'Upload Image'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Section: Track Details (audio only) */}
                            {media.type === 'audio' && (
                                <div>
                                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Track Details</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">Artist</label>
                                            <input
                                                type="text"
                                                value={editFormData.artist}
                                                onChange={(e) => setEditFormData({...editFormData, artist: e.target.value})}
                                                className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                                placeholder="Artist name"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">Album</label>
                                            <input
                                                type="text"
                                                value={editFormData.album}
                                                onChange={(e) => setEditFormData({...editFormData, album: e.target.value})}
                                                className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                                placeholder="Album name"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">Year</label>
                                            <input
                                                type="number"
                                                value={editFormData.year}
                                                onChange={(e) => setEditFormData({...editFormData, year: e.target.value})}
                                                className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                                placeholder="2024"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">BPM</label>
                                            <input
                                                type="number"
                                                value={editFormData.bpm}
                                                onChange={(e) => setEditFormData({...editFormData, bpm: e.target.value})}
                                                className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                                placeholder="120"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">Key</label>
                                            <input
                                                type="text"
                                                value={editFormData.key_signature}
                                                onChange={(e) => setEditFormData({...editFormData, key_signature: e.target.value})}
                                                className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                                placeholder="C Major"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">Track #</label>
                                            <input
                                                type="number"
                                                value={editFormData.track_number}
                                                onChange={(e) => setEditFormData({...editFormData, track_number: e.target.value})}
                                                className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                                placeholder="1"
                                            />
                                        </div>
                                        <div className="relative" ref={genreDropdownRef}>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">Genres</label>
                                            <div className="mb-2 flex flex-wrap gap-1.5">
                                                {editFormData.genre.map(genre => (
                                                    <span key={genre} className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-600 text-white text-xs rounded-full">
                                                        {genre}
                                                        <button type="button" onClick={() => removeGenre(genre)} aria-label={`Remove ${genre} genre`} className="text-purple-200 hover:text-white ml-0.5">
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setGenreDropdownOpen(!genreDropdownOpen)}
                                                className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm flex items-center justify-between text-left focus:ring-2 focus:ring-purple-500"
                                            >
                                                <span className={editFormData.genre.length === 0 ? 'text-gray-400' : 'text-white'}>
                                                    {editFormData.genre.length === 0 ? 'Select genres...' : `${editFormData.genre.length} selected`}
                                                </span>
                                                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${genreDropdownOpen ? 'rotate-180' : ''}`} />
                                            </button>
                                            {genreDropdownOpen && (
                                                <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto">
                                                    {genres.map(genre => (
                                                        <button key={genre} type="button" onClick={() => toggleGenre(genre)}
                                                            className="w-full px-3 py-2 text-left text-white hover:bg-gray-700 transition-colors text-sm flex items-center justify-between">
                                                            <span>{genre}</span>
                                                            {editFormData.genre.includes(genre) && <Check className="w-4 h-4 text-purple-400" />}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Section: Access Control */}
                            <div>
                                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Access Control</h3>
                                <PrivacySettings
                                    value={editFormData.accessLevel}
                                    password={editFormData.accessPassword}
                                    passwordHint={editFormData.accessHint}
                                    isPaid={editFormData.isPaid}
                                    price={editFormData.price}
                                    currency={editFormData.currency}
                                    onChange={(v) => setEditFormData(prev => ({ ...prev, accessLevel: v }))}
                                    onPasswordChange={(v) => setEditFormData(prev => ({ ...prev, accessPassword: v }))}
                                    onHintChange={(v) => setEditFormData(prev => ({ ...prev, accessHint: v }))}
                                    onIsPaidChange={(v) => setEditFormData(prev => ({ ...prev, isPaid: v }))}
                                    onPriceChange={(v) => setEditFormData(prev => ({ ...prev, price: v }))}
                                    onCurrencyChange={(v) => setEditFormData(prev => ({ ...prev, currency: v }))}
                                />
                            </div>

                            {/* Section: Copyright */}
                            <div>
                                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Copyright</h3>
                                <CopyrightSettings
                                    isCopyrighted={editFormData.isCopyrighted}
                                    copyrightText={editFormData.copyrightText}
                                    onIsCopyrightedChange={(v) => setEditFormData(prev => ({ ...prev, isCopyrighted: v }))}
                                    onCopyrightTextChange={(v) => setEditFormData(prev => ({ ...prev, copyrightText: v }))}
                                />
                            </div>

                            {/* Section: Contributing Artists */}
                            <div>
                                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Contributing Artists</h3>
                                <ContributingArtistSelector
                                    selectedArtists={editFormData.contributingArtists || []}
                                    onArtistsChange={(artists) => setEditFormData({...editFormData, contributingArtists: artists})}
                                />
                            </div>

                            {/* Section: Links */}
                            <div>
                                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Links</h3>
                                <div className="space-y-3">
                                    {editFormData.hyperlinks.map((link, index) => (
                                        <div key={index} className="flex gap-3 items-center p-3 bg-gray-800/60 rounded-lg">
                                            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                <input
                                                    type="text"
                                                    placeholder="Label (e.g. Spotify, YouTube)"
                                                    value={link.label}
                                                    onChange={(e) => {
                                                        const updated = [...editFormData.hyperlinks];
                                                        updated[index].label = e.target.value;
                                                        setEditFormData({...editFormData, hyperlinks: updated});
                                                    }}
                                                    className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                                />
                                                <input
                                                    type="url"
                                                    placeholder="URL (https://...)"
                                                    value={link.url}
                                                    onChange={(e) => {
                                                        const updated = [...editFormData.hyperlinks];
                                                        updated[index].url = e.target.value;
                                                        setEditFormData({...editFormData, hyperlinks: updated});
                                                    }}
                                                    className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setEditFormData({...editFormData, hyperlinks: editFormData.hyperlinks.filter((_, i) => i !== index)})}
                                                className="p-2 bg-red-600/80 hover:bg-red-600 rounded-lg text-white transition-colors flex-shrink-0"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => setEditFormData({...editFormData, hyperlinks: [...editFormData.hyperlinks, { type: 'custom', label: '', url: '' }]})}
                                        className="w-full p-3 border-2 border-dashed border-gray-600 hover:border-purple-500 rounded-lg text-gray-400 hover:text-purple-400 transition-colors flex items-center justify-center gap-2 text-sm"
                                    >
                                        <Link className="w-4 h-4" /> Add Link
                                    </button>
                                </div>
                            </div>

                            {/* Bottom actions */}
                            <div className="flex justify-end gap-3 pt-6 border-t border-gray-700">
                                <button onClick={handleCancelEdit} className="flex items-center gap-2 px-5 py-2.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors">
                                    <X className="w-4 h-4" /> Cancel
                                </button>
                                <button onClick={handleSaveEdit} className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 rounded-lg text-white transition-colors font-semibold">
                                    <Save className="w-4 h-4" /> Save Changes
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* ── Normal view ── */
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Main Info */}
                            <div className="lg:col-span-2">
                                <h1 className="text-3xl font-bold mb-2">{media.title}</h1>
                                {!!media.is_copyrighted && media.copyright_text && (
                                    <p className="text-xs text-gray-500 mb-2">{media.copyright_text}</p>
                                )}
                                {(isSoftGated || media.post_id || media.containing_album_id) ? (
                                    <div className="flex flex-wrap items-center gap-2 mb-4">
                                        {isSoftGated && (
                                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-purple-500/15 text-purple-300 border border-purple-500/30">
                                                <Lock className="w-3 h-3" />
                                                {getAccessLabel(softGateAccessLevel, media?.type === 'video' ? 'view' : 'listen')}
                                            </span>
                                        )}
                                        {media.post_id && (
                                            <a
                                                href={`/view/post/${media.username}/${media.post_id}`}
                                                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-500/15 text-blue-300 border border-blue-500/30 hover:bg-blue-500/25 transition-colors"
                                            >
                                                <FileText className="w-3 h-3" />
                                                View full post →
                                            </a>
                                        )}
                                        {media.containing_album_id && (
                                            <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                                                <Disc3 className="w-3 h-3" />
                                                Media from album "{media.containing_album_title}"
                                                <a
                                                    href={`/album/${media.username}/${media.containing_album_slug || media.containing_album_id}`}
                                                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-pink-500/15 text-pink-300 border border-pink-500/30 hover:bg-pink-500/25 transition-colors"
                                                >
                                                    View full album →
                                                </a>
                                            </span>
                                        )}
                                    </div>
                                ) : (
                                    <div className="mb-4" />
                                )}

                                <div className="mb-6">
                                    <div className="flex items-center gap-1 mb-3 border-b border-gray-700">
                                        <button
                                            type="button"
                                            onClick={() => setDescriptionTab('description')}
                                            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-colors border-b-2 ${
                                                descriptionTab === 'description'
                                                    ? 'text-purple-400 border-purple-400'
                                                    : 'text-gray-400 border-transparent hover:text-gray-300'
                                            }`}
                                        >
                                            <FileText className="w-4 h-4" />
                                            Description
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setDescriptionTab('lyrics')}
                                            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-colors border-b-2 ${
                                                descriptionTab === 'lyrics'
                                                    ? 'text-purple-400 border-purple-400'
                                                    : 'text-gray-400 border-transparent hover:text-gray-300'
                                            }`}
                                        >
                                            <FileText className="w-4 h-4" />
                                            Lyrics
                                        </button>
                                    </div>
                                    {descriptionTab === 'description' ? (
                                        media.description ? (
                                            <p className="text-gray-400 whitespace-pre-wrap leading-relaxed">{media.description}</p>
                                        ) : (
                                            <p className="text-gray-500 italic">No description</p>
                                        )
                                    ) : (
                                        media.lyrics ? (
                                            <p className="text-gray-400 whitespace-pre-wrap leading-relaxed">{media.lyrics}</p>
                                        ) : (
                                            <p className="text-gray-500 italic">No lyrics added</p>
                                        )
                                    )}
                                </div>

                                {/* Creator Info */}
                                <div className="flex items-center gap-4 p-4 bg-gray-700/30 rounded-lg">
                                    {media.profile_picture_url ? (
                                        <img src={media.profile_picture_url} alt={media.display_name || media.username} width="64" height="64" className="w-16 h-16 rounded-full object-cover" />
                                    ) : (
                                        <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                                            <span className="text-white font-semibold text-xl">{media.display_name?.charAt(0) || media.username?.charAt(0) || 'U'}</span>
                                        </div>
                                    )}
                                    <div>
                                        <p className="font-semibold text-lg">{media.display_name || media.username}</p>
                                        <p className="text-gray-400">@{media.username}</p>
                                        <a href={`/profile/${media.username}`} className="text-purple-400 hover:text-purple-300 text-sm">View Profile →</a>
                                    </div>
                                </div>

                                {/* Hyperlinks */}
                                {hyperlinks.length > 0 && (
                                    <div className="mt-6 p-4 bg-gray-700/30 rounded-lg">
                                        <h3 className="text-lg font-semibold text-gray-300 mb-3 flex items-center gap-2">
                                            <Link className="w-5 h-5" /> Links
                                        </h3>
                                        <div className="space-y-2">
                                            {hyperlinks.map((link, index) => (
                                                <a key={index} href={link.url} target="_blank" rel="noopener noreferrer"
                                                    className="flex items-center gap-3 p-3 bg-gray-600/30 hover:bg-gray-600/50 rounded-lg transition-colors group">
                                                    <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center group-hover:bg-purple-500/30 transition-colors">
                                                        <Link className="w-4 h-4 text-purple-400" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="font-medium text-white group-hover:text-purple-300 transition-colors">{link.label}</div>
                                                        <div className="text-sm text-gray-400 url-break">{link.url}</div>
                                                    </div>
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Sidebar */}
                            <div className="bg-gray-700/30 rounded-lg p-4">
                                <h3 className="font-semibold mb-4 text-gray-300 flex items-center gap-2">
                                    <FileText className="w-5 h-5" /> Details
                                </h3>
                                <div className="space-y-3 text-sm">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-400">Type:</span>
                                        <span className="capitalize font-medium">{media.type}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-400">Size:</span>
                                        <span>{(media.file_size / 1024 / 1024).toFixed(1)} MB</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-400">Format:</span>
                                        <span>{media.mime_type?.split('/')[1]?.toUpperCase()}</span>
                                    </div>

                                    {media.type === 'audio' && (
                                        <div className="pt-3 border-t border-gray-600 space-y-3">
                                            {media.artist && <div className="flex justify-between"><span className="text-gray-400">Artist:</span><span className="font-medium">{media.artist}</span></div>}
                                            {media.album && <div className="flex justify-between"><span className="text-gray-400">Album:</span><span className="font-medium">{media.album}</span></div>}
                                            {media.genre && <div className="flex justify-between"><span className="text-gray-400">Genre:</span><span className="font-medium">{media.genre}</span></div>}
                                            {media.year && <div className="flex justify-between"><span className="text-gray-400">Year:</span><span className="font-medium">{media.year}</span></div>}
                                            {media.bpm && <div className="flex justify-between"><span className="text-gray-400">BPM:</span><span className="font-medium">{media.bpm}</span></div>}
                                            {media.key_signature && <div className="flex justify-between"><span className="text-gray-400">Key:</span><span className="font-medium">{media.key_signature}</span></div>}
                                            {media.track_number && <div className="flex justify-between"><span className="text-gray-400">Track #:</span><span className="font-medium">{media.track_number}</span></div>}
                                            {media.duration && <div className="flex justify-between"><span className="text-gray-400">Duration:</span><span className="font-medium">{Math.floor(media.duration / 60)}:{(media.duration % 60).toString().padStart(2, '0')}</span></div>}
                                        </div>
                                    )}

                                    <div className="pt-3 border-t border-gray-600">
                                        <div className="flex items-center gap-2 mb-1 text-gray-400">
                                            <Calendar className="w-4 h-4" /> Uploaded
                                        </div>
                                        <div className="text-gray-300 text-xs">
                                            {new Date(media.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                        </div>
                                    </div>

                                    {parseTagsSafely(media.tags).length > 0 && (
                                        <div className="pt-3 border-t border-gray-600">
                                            <div className="flex items-center gap-2 mb-2 text-gray-400">
                                                <Tag className="w-4 h-4" /> Tags
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {parseTagsSafely(media.tags).map((tag, index) => (
                                                    <span key={index} className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full">#{tag}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Access level badge */}
                                    {media.access_level && media.access_level !== 'public' && (
                                        <div className="pt-3 border-t border-gray-600 flex justify-between items-center">
                                            <span className="text-gray-400">Access:</span>
                                            <span className="capitalize text-yellow-300 text-xs font-medium bg-yellow-500/10 px-2 py-0.5 rounded-full">
                                                {media.access_level.replace(/_/g, ' ')}
                                            </span>
                                        </div>
                                    )}

                                    {/* Contributors */}
                                    <div className="pt-3 border-t border-gray-600">
                                        <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                                            <Users className="w-4 h-4" /> Contributors
                                        </h4>
                                        <div className="flex items-center gap-3 p-2 bg-gray-700/30 rounded-lg mb-2">
                                            {media.profile_picture_url ? (
                                                <img src={media.profile_picture_url} alt={media.display_name || media.username} width="28" height="28" className="w-7 h-7 rounded-full object-cover" />
                                            ) : (
                                                <div className="w-7 h-7 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                                                    <span className="text-white font-semibold text-xs">{media.display_name?.charAt(0) || 'U'}</span>
                                                </div>
                                            )}
                                            <div>
                                                <p className="font-medium text-white text-xs">{media.display_name || media.username}</p>
                                                <p className="text-gray-400 text-xs">Creator</p>
                                            </div>
                                        </div>
                                        <ContributingArtistsDisplay
                                            mediaType="upload"
                                            mediaId={media.id}
                                            showTitle={false}
                                            layout="vertical"
                                            maxVisible={null}
                                        />
                                    </div>

                                    {/* Stats */}
                                    <div className="pt-3 border-t border-gray-600 space-y-2">
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="text-gray-400">Likes:</span>
                                            <span className="text-red-300 font-medium">{media?.like_count || 0}</span>
                                        </div>
                                        {mediaStats && (
                                            <>
                                                {(media?.type === 'audio' || media?.type === 'video') && (
                                                    <div className="flex justify-between items-center text-xs">
                                                        <span className="text-gray-400">Plays:</span>
                                                        <span className="text-purple-300 font-medium">{mediaStats.playCount.toLocaleString()}</span>
                                                    </div>
                                                )}
                                                <div className="flex justify-between items-center text-xs">
                                                    <span className="text-gray-400">Views:</span>
                                                    <span className="text-blue-300 font-medium">{mediaStats.viewCount.toLocaleString()}</span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Comments Section */}
                <div className="mt-8">
                    <h3 className="text-xl font-bold text-white mb-6">
                        Comments ({media?.comment_count || 0})
                    </h3>

                    {/* Add Comment Form */}
                    {isAuthenticated ? (
                        <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
                            <form onSubmit={handleAddComment}>
                                <textarea
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    placeholder="Add a comment..."
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg p-4 text-white placeholder-gray-400 resize-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                                    rows="3"
                                />
                                <div className="flex justify-end mt-4">
                                    <button
                                        type="submit"
                                        disabled={!newComment.trim() || submittingComment}
                                        className="px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:opacity-50 text-white rounded-lg transition-colors"
                                    >
                                        {submittingComment ? 'Posting...' : 'Post Comment'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    ) : (
                        <div className="bg-gray-800/50 rounded-lg p-6 mb-6 text-center">
                            <div className="text-4xl mb-4">💬</div>
                            <h3 className="text-xl font-bold text-white mb-2">Join the conversation</h3>
                            <p className="text-gray-400 mb-6">
                                Login to share your thoughts and connect with other members of The Deep Wave community.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <a
                                    href={`/login?redirect=${encodeURIComponent(currentPagePath())}`}
                                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium text-center"
                                >
                                    Login to Comment
                                </a>
                                <a
                                    href={`/register?redirect=${encodeURIComponent(currentPagePath())}`}
                                    className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium text-center"
                                >
                                    Create Account
                                </a>
                            </div>
                        </div>
                    )}

                    {/* Comments List */}
                    <div className="space-y-4">
                        {loadingComments ? (
                            <div className="text-center py-8">
                                <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
                                <p className="text-gray-400">Loading comments...</p>
                            </div>
                        ) : comments.length > 0 ? (
                            comments.map((comment) => (
                                <div key={comment.id} className="bg-gray-800/30 rounded-lg p-6">
                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center flex-shrink-0">
                                            {comment.avatar ? (
                                                <img
                                                    src={comment.avatar}
                                                    alt={comment.display_name}
                                                    width="40"
                                                    height="40"
                                                    className="w-10 h-10 rounded-full object-cover"
                                                />
                                            ) : (
                                                <span className="text-white font-semibold text-sm">
                                                    {(comment.display_name || comment.username)?.charAt(0) || 'U'}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <h4 className="font-semibold text-white">
                                                    {comment.display_name || comment.username}
                                                </h4>
                                                <span className="text-sm text-gray-500">
                                                    {formatTimeAgo(comment.created_at)}
                                                </span>
                                            </div>
                                            <p className="text-gray-300 leading-relaxed mb-3">
                                                {comment.content}
                                            </p>
                                            <button
                                                onClick={() => handleCommentLike(comment.id)}
                                                className={`flex items-center gap-2 px-3 py-1 rounded-lg transition-colors text-sm ${
                                                    comment.user_has_liked
                                                        ? 'text-red-400 bg-red-400/10 hover:bg-red-400/20' 
                                                        : 'text-gray-400 hover:text-red-400 hover:bg-red-400/10'
                                                }`}
                                            >
                                                <Heart className={`w-4 h-4 ${comment.user_has_liked ? 'fill-current' : ''}`} />
                                                <span>{comment.like_count || 0}</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8">
                                <MessageCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                                <p className="text-gray-400">No comments yet. Be the first to comment!</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* More content from The Deep Wave */}
                {media?.type === 'audio' && (
                    <div className="mt-8">
                        <h2 className="text-2xl font-bold text-white mb-4">More Audio</h2>
                        <LatestDeepWaveSongs limit={4} showTitle={false} columns={4} excludeId={media?.id} />
                    </div>
                )}
                {media?.type === 'video' && (
                    <div className="mt-8">
                        <h2 className="text-2xl font-bold text-white mb-4">More Video</h2>
                        <LatestDeepWaveVideos limit={4} showTitle={false} columns={4} excludeId={media?.id} />
                    </div>
                )}
                {media?.type === 'image' && (
                    <div className="mt-8">
                        <h2 className="text-2xl font-bold text-white mb-4">More Images</h2>
                        <LatestDeepWaveImages limit={4} showTitle={false} columns={4} excludeId={media?.id} />
                    </div>
                )}
            </div>

            {/* Image Editor Modal */}
            {showImageEditor && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[95vh]">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-white">Edit Cover Image</h3>
                            <button
                                onClick={() => setShowImageEditor(false)}
                                className="w-8 h-8 rounded-lg bg-gray-700 hover:bg-gray-600 flex items-center justify-center transition-colors"
                            >
                                <X className="w-4 h-4 text-gray-400" />
                            </button>
                        </div>

                        {/* Current Image Preview */}
                        {media.cover_image_url && (
                            <div className="mb-6">
                                <h4 className="text-sm font-medium text-gray-300 mb-3">Current Image</h4>
                                <div className="relative inline-block">
                                    <img
                                        src={media.cover_image_url}
                                        alt="Current cover"
                                        width="192"
                                        height="192"
                                        className="w-48 h-48 object-cover rounded-lg"
                                    />
                                    <button
                                        onClick={handleRemoveImage}
                                        disabled={uploadingImage}
                                        className="absolute top-2 right-2 p-1.5 bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
                                        title="Remove image"
                                    >
                                        <Trash2 className="w-4 h-4 text-white" />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Upload New Image */}
                        <div>
                            <h4 className="text-sm font-medium text-gray-300 mb-3">
                                {media.cover_image_url ? 'Replace Image' : 'Upload Image'}
                            </h4>
                            <FileUpload
                                type="image"
                                onUpload={handleImageUpload}
                                onError={(error) => {
                                    console.error('Image upload error:', error);
                                    alert('Failed to upload image. Please try again.');
                                }}
                                maxSizeMB={10}
                                accept="image/*"
                                disabled={uploadingImage}
                            >
                                <div className="flex items-center gap-3">
                                    <Image className="w-8 h-8 text-purple-400" />
                                    <div>
                                        <p className="text-purple-400 font-semibold">
                                            {uploadingImage ? 'Uploading...' : 'Choose Image'}
                                        </p>
                                        <p className="text-gray-400 text-sm">
                                            JPG, PNG, WebP (Max 10MB)
                                        </p>
                                    </div>
                                </div>
                            </FileUpload>
                        </div>

                        {/* Tips */}
                        <div className="mt-6 p-4 bg-gray-700/30 rounded-lg">
                            <h5 className="text-sm font-medium text-gray-300 mb-2">Tips for best results:</h5>
                            <ul className="text-sm text-gray-400 space-y-1">
                                <li>• Use square images (1:1 ratio) for best appearance</li>
                                <li>• Recommended resolution: 800x800 pixels or higher</li>
                                <li>• High contrast images work better with overlay text</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-xl font-bold text-white mb-4">Delete Media</h3>
                        <p className="text-gray-300 mb-6">
                            Are you sure you want to delete "{media.title}"? This action cannot be undone.
                        </p>
                        <div className="flex gap-4 justify-end">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Fullscreen Image Overlay */}
            {isImageExpanded && type === 'image' && media && (
                <div
                    className="fixed inset-0 z-50 bg-black flex items-center justify-center"
                    onClick={() => setIsImageExpanded(false)}
                >
                    <img
                        src={media.file_url}
                        alt={media.title}
                        className={`max-w-full max-h-full object-contain select-none ${zoomScale > 1 ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in'}`}
                        onClick={(e) => e.stopPropagation()}
                        onWheel={handleImageZoomWheel}
                        onMouseDown={handleImageZoomMouseDown}
                        onDoubleClick={handleImageZoomDoubleClick}
                        onContextMenu={(e) => e.preventDefault()}
                        onDragStart={(e) => e.preventDefault()}
                        style={{
                            transform: `scale(${zoomScale}) translate(${zoomOffset.x / zoomScale}px, ${zoomOffset.y / zoomScale}px)`,
                            transition: dragStateRef.current ? 'none' : 'transform 150ms ease-out',
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            MozUserSelect: 'none',
                            msUserSelect: 'none',
                            WebkitTouchCallout: 'none',
                            WebkitUserDrag: 'none'
                        }}
                    />

                    <span className="absolute top-4 left-4 px-3 py-1.5 bg-black/50 backdrop-blur-sm rounded-lg text-white/70 text-xs pointer-events-none">
                        Scroll to zoom · drag to pan · double-click to reset
                    </span>
                </div>
            )}

            {/* Download Paywall Modal */}
            <DownloadPaywall
                isOpen={downloadPaywall.isOpen}
                onClose={() => setDownloadPaywall({ isOpen: false, media: null })}
                mediaTitle={downloadPaywall.media?.title || 'Unknown Media'}
                mediaType={type === 'audio' ? 'song' : type}
                uploadId={downloadPaywall.media?.id}
            />

            {/* Share Modal */}
            <ShareModal
                isOpen={shareModal.isOpen}
                onClose={() => setShareModal({ isOpen: false, media: null })}
                mediaTitle={shareModal.media?.title || 'Unknown Media'}
                mediaType={type}
                username={shareModal.media?.username}
                mediaId={shareModal.media?.stored_filename || shareModal.media?.original_filename || shareModal.media?.id}
                artistName={shareModal.media?.display_name || shareModal.media?.username}
            />
            
            {/* Signup Invite Modal */}
            <SignupInviteModal
                isOpen={signupInvite.isOpen}
                onClose={() => setSignupInvite({ isOpen: false, contentType: 'audio' })}
                contentType={signupInvite.contentType}
            />

            <AccessRequiredModal
                isOpen={showAccessModal}
                onClose={() => setShowAccessModal(false)}
                accessLevel={softGateAccessLevel}
                action={media?.type === 'video' ? 'view' : 'listen'}
                isAuthenticated={isAuthenticated}
                viewUrl={null}
            />
        </div>
    );
};

export default MediaDisplay;